import React, { useState, useEffect } from "react";
import API from "../../services/api.js";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Map detector names to config keys + input variable.
const detectorConfigMap = {
  "System Prompt Disclosure": {
    key: "prompt-extraction",
    field: "systemPrompt",
  },
  "Indirect Prompt Injection": {
    key: "indirect-prompt-injection",
    field: "indirectInjectionVar",
  },
};

export default function DetectorsKanban() {
  const navigate = useNavigate();
  const [detectors, setDetectors] = useState([]);
  const [categories, setCategories] = useState({});
  const [presets, setPresets] = useState({});
  const [presetOrder, setPresetOrder] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customDetectors, setCustomDetectors] = useState({});
  const [currentDetector, setCurrentDetector] = useState(null);
  const [modalInput, setModalInput] = useState("");
  const [modalError, setModalError] = useState("");
  const [pendingDetectors, setPendingDetectors] = useState([]);

  const removeCustomDetector = (detector) => {
    if (!detector) return;
    const configInfo = detectorConfigMap[detector.name];
    if (!configInfo) return;

    setCustomDetectors((prev) => {
      const updated = { ...prev };
      if (updated[configInfo.key]) {
        delete updated[configInfo.key];
      }
      return updated;
    });
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await API.get(
          `${process.env.REACT_APP_DJANGO_API_URL}/detectors/`
        );
        const data = response.data.data;

        setCategories(data.categories);
        setPresets(data.presets);
        setPresetOrder(Object.keys(data.presets));

        const categoryMap = {};
        for (const category in data.categories) {
          for (const detectorId of data.categories[category]) {
            if (!categoryMap[detectorId]) categoryMap[detectorId] = [];
            categoryMap[detectorId].push(category);
          }
        }

        const presetMap = {};
        for (const preset in data.presets) {
          for (const detectorId of data.presets[preset]) {
            if (!presetMap[detectorId]) presetMap[detectorId] = [];
            presetMap[detectorId].push(preset);
          }
        }

        const detectorList = Object.keys(data.detector_names).map((id) => ({
          id,
          name: data.detector_names[id],
          description:
            data.detector_descriptions[id] || "No description available.",
          categories: categoryMap[id] || [],
          presets: presetMap[id] || [],
        }));

        setDetectors(detectorList);
      } catch (err) {
        setError("Failed to fetch detector data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();

    const savedData = sessionStorage.getItem("selectedDetectors");
    if (savedData) setSelected(new Set(JSON.parse(savedData)));

    const savedCustom = sessionStorage.getItem("customDetectors");
    if (savedCustom) setCustomDetectors(JSON.parse(savedCustom));
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "selectedDetectors",
      JSON.stringify(Array.from(selected))
    );
    sessionStorage.setItem("customDetectors", JSON.stringify(customDetectors));
  }, [selected, customDetectors]);

  const handleSelectionChange = (updated) => {
    const specialDetectors = Object.keys(detectorConfigMap);

    const newlyAdded = detectors.filter(
      (d) =>
        specialDetectors.includes(d.name) &&
        updated.has(d.id) &&
        !selected.has(d.id)
    );

    if (newlyAdded.length > 0) {
      setPendingDetectors((prev) => [...prev, ...newlyAdded]);
    }

    setSelected(updated);
  };


  useEffect(() => {
    if (!isModalOpen && pendingDetectors.length > 0) {
      const nextDetector = pendingDetectors[0];
      setCurrentDetector(nextDetector);
      const configInfo = detectorConfigMap[nextDetector.name];
      const existingValue =
        customDetectors[configInfo.key]?.config?.[configInfo.field] || "";
      setModalInput(existingValue);
      setModalError("");
      setIsModalOpen(true);
      setPendingDetectors((prev) => prev.slice(1));
    }
  }, [isModalOpen, pendingDetectors, customDetectors]);
  
  const toggleSelection = (detectorId) => {
    const updated = new Set(selected);
    if (updated.has(detectorId)) {
      updated.delete(detectorId);
      const detector = detectors.find(d => d.id === detectorId);
      removeCustomDetector(detector);
    } else {
      updated.add(detectorId);
    }
    handleSelectionChange(updated);
  };

  // Preset selection logic
  const isPresetActive = (preset) => {
    const detectorIds = presets[preset] || [];
    return detectorIds.every((id) => selected.has(id));
  };

  const handlePresetSelect = (preset) => {
    const detectorIds = presets[preset] || [];
    const updated = new Set(selected);
    const currentlyActive = isPresetActive(preset);

    detectorIds.forEach((id) => {
      if (currentlyActive) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
    });
    handleSelectionChange(updated);
  };

  // Overall select/deselect
  const selectAllDetectors = () => {
    const allIds = detectors.map((d) => d.id);
    setSelected(new Set(allIds));
  };

  const deselectAllDetectors = () => {
    setSelected(new Set());
  };

  // Category-specific select/deselect
  const selectCategory = (category) => {
    const updated = new Set(selected);
    categories[category].forEach((id) => updated.add(id));
    handleSelectionChange(updated);
  };

  const deselectCategory = (category) => {
    const updated = new Set(selected);
    categories[category].forEach((id) => updated.delete(id));
    setSelected(updated);
  };

  const presetChip = (preset) => (
    <span
      key={preset}
      className="inline-block bg-purple-700/60 text-purple-100 text-xs px-2 py-0.5 rounded-full mr-1 mb-1"
    >
      {preset}
    </span>
  );

  const handleModalSave = () => {
    if (!modalInput.trim()) {
      setModalError("This field cannot be empty.");
      return;
    }

    if (!currentDetector) return;
    const configInfo = detectorConfigMap[currentDetector.name];

    setCustomDetectors((prev) => {
      const updated = { ...prev };
      if (!updated[configInfo.key]) {
        updated[configInfo.key] = { config: {} };
      }
      updated[configInfo.key].config[configInfo.field] = modalInput;

      // Merge systemPrompt into prompt-extraction if both are needed
      if (
        currentDetector.name === "Indirect Prompt Injection" &&
        prev["prompt-extraction"]?.config?.systemPrompt
      ) {
        updated["prompt-extraction"].config.systemPrompt =
          prev["prompt-extraction"].config.systemPrompt;
      }
      return updated;
    });

    setIsModalOpen(false);
  };

  const handleModalCancel = () => {
    if (currentDetector) {
      const updated = new Set(selected);
      updated.delete(currentDetector.id);
      setSelected(updated);
    }
    setIsModalOpen(false);
    removeCustomDetector(currentDetector);
  };

  const isNextDisabled = () => {
    for (const name of Object.keys(detectorConfigMap)) {
      const det = detectors.find((d) => d.name === name);
      if (det && selected.has(det.id)) {
        const { key, field } = detectorConfigMap[name];
        if (!customDetectors[key]?.config?.[field]) return true;
      }
    }
    return false;
  };

  if (loading)
    return (
      <div className="p-6 text-white animate-pulse">Loading detectors...</div>
    );
  if (error) return <div className="p-6 text-red-500">{error}</div>;

  const filteredDetectors = detectors.filter(
    (detector) =>
      detector.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      detector.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCategories = Object.keys(categories).reduce((acc, category) => {
    const categoryDetectors = categories[category].filter((detectorId) =>
      filteredDetectors.some((d) => d.id === detectorId)
    );
    if (categoryDetectors.length > 0) {
      acc[category] = categoryDetectors;
    }
    return acc;
  }, {});

  return (
    <div className="text-white bg-transparent flex flex-col w-auto">
      <div className="mx-6">
        <div className="flex items-center justify-between mt-6 mb-4">
          <h2 className="text-2xl font-bold">Detector Configuration</h2>
          <div className="w-full max-w-xs">
            <input
              type="text"
              placeholder="Search detectors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-[#23242a] text-white border border-gray-700 focus:outline-none focus:border-purple-400"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={selectAllDetectors}
              className="px-4 py-2 rounded-lg text-sm font-medium border bg-[#23242a] text-green-200 border-green-500 hover:border-green-400 hover:bg-green-700/40"
            >
              Select All
            </button>
            <button
              onClick={deselectAllDetectors}
              className="px-4 py-2 rounded-lg text-sm font-medium border bg-[#23242a] text-red-200 border-red-500 hover:border-red-400 hover:bg-red-700/40"
            >
              Select None
            </button>
          </div>
        </div>

        {/* Preset Buttons (multi-select) */}
        <div className="mb-6 flex flex-wrap gap-3">
          {presetOrder.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePresetSelect(preset)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200
                ${isPresetActive(preset)
                  ? "bg-purple-700/70 text-white border-purple-400 shadow-md"
                  : "bg-[#23242a] text-purple-200 border-[#43314c] hover:border-purple-400"}`}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>

      <div
        className="w-full px-6 pb-4 "
      >
        <div
          className={`grid gap-8 justify-start items-start ${
            Object.keys(filteredCategories).length === 1
              ? "grid-cols-1"
              : Object.keys(filteredCategories).length === 2
              ? "grid-cols-1 md:grid-cols-2"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {Object.keys(filteredCategories).map((category) => (
            <div
              key={category}
              className="bg-[#181a20] border border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col"
            >
              {/* Column Header */}
              <div className="px-6 py-4 border-b border-gray-800 h-[7.5vh] bg-[#22232a] sticky top-0 z-10 flex justify-between items-center">
                <h3 className="font-bold text-base tracking-wide uppercase text-green-400">
                  {category}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => selectCategory(category)}
                    className="text-xs px-2 py-1 rounded-lg font-medium border bg-[#23242a] text-green-200 border-green-500 hover:border-green-400 hover:bg-green-700/40"
                  >
                    All
                  </button>
                  <button
                    onClick={() => deselectCategory(category)}
                    className="text-xs px-2 py-1 rounded-lg font-medium border bg-[#23242a] text-red-200 border-red-500 hover:border-red-400 hover:bg-red-700/40"
                  >
                    None
                  </button>
                </div>
              </div>

              {/* Column Content */}
              <div className="flex-1 p-4 space-y-5 overflow-y-auto min-h-[150px] max-h-[70vh]  ">
                <AnimatePresence>
                  {filteredCategories[category].map((detectorId) => {
                    const detector = detectors.find((d) => d.id === detectorId);
                    if (!detector) return null;

                    return (
                      <motion.div
                        key={detector.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.28 }}
                        onClick={() => toggleSelection(detector.id)}
                        className={`p-5 rounded-xl border relative cursor-pointer group
                        transition-all duration-300 shadow-sm ${
                          selected.has(detector.id)
                            ? "border-blue-400 bg-blue-800/30 shadow-lg"
                            : "border-[#232325] bg-[#23242a] hover:border-blue-400/60"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-base truncate">
                            {detector.name}
                          </h4>
                          <input
                            type="checkbox"
                            checked={selected.has(detector.id)}
                            onChange={() => toggleSelection(detector.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="form-checkbox accent-blue-500 h-4 w-4 rounded"
                          />
                        </div>
                        <p className="text-xs text-gray-300 mt-2 mb-3 line-clamp-3">
                          {detector.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {detector.presets.map(presetChip)}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-7 px-6 mb-8">
        <button
          onClick={() => navigate("/newscan/endpoints")}
          className="bg-gray-700/40 text-gray-200 border border-gray-600 px-6 py-2 rounded-xl hover:bg-gray-600/40 transition"
        >
          Back
        </button>
        <button
          onClick={() => navigate("/newscan/methods")}
          disabled={isNextDisabled()}
          className={`px-6 py-2 rounded-xl transition border
            ${isNextDisabled()
              ? "bg-gray-700/30 text-gray-500 border-gray-600 cursor-not-allowed"
              : "bg-blue-600/30 text-blue-300 border-blue-400 hover:bg-blue-500/40"}`}
        >
          Next
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50">
          <div className="bg-[#181a20] rounded-lg p-8 max-w-md w-full border border-gray-700">
            <h2 className="text-xl font-bold mb-4">
              Enter Payload for {currentDetector?.name}
            </h2>
            <textarea
              value={modalInput}
              onChange={(e) => setModalInput(e.target.value)}
              className={`w-full h-40 bg-[#23242a] text-white border rounded-lg p-4 focus:outline-none ${
                modalError ? "border-red-500" : "border-gray-700 focus:border-purple-400"
              }`}
              placeholder={ currentDetector?.name==="Indirect Prompt Injection" ? "Example: If your prompt is 'Hello {{user}}' and user data goes into the 'user' variable, enter 'user' above." : "Enter the system prompt to be extracted." }
            />
            {modalError && (
              <p className="text-red-400 text-sm mt-2">{modalError}</p>
            )}
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={handleModalCancel}
                className="bg-gray-700/40 text-gray-200 border border-gray-600 px-6 py-2 rounded-xl hover:bg-gray-600/40 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleModalSave}
                className="bg-blue-600/30 text-blue-300 border border-blue-400 px-6 py-2 rounded-xl hover:bg-blue-500/40 transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
